// Clamping that turns a product to face the wall it is placed against.
import * as THREE from 'three';
import {
    RoomPlannerProduct,
    RoomType,
} from 'components/customer/RoomPlanner/types';
import {store} from 'store/customer';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import {
    getDirection,
    getGroupWidth,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {hasDoors} from 'components/customer/RoomPlanner/helpers/doorDetection';
import {getWallSurfaceBox} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {
    isCornerLShapedCabinet,
    isNonRectShapeProduct,
} from 'components/customer/RoomPlanner/helpers/shapeProducts';

const getRoomDimensions = (roomType: RoomType) => {
    const state = store.getState();
    const {rectangularDimension, lShapedDimension} = state.roomPlanner;
    const roomWidth =
        roomType === 'RECTANGULAR'
            ? rectangularDimension.width
            : lShapedDimension.rightWidth;
    const roomDepth =
        roomType === 'RECTANGULAR'
            ? rectangularDimension.length
            : lShapedDimension.leftWidth;
    const halfRoomWidth = (roomWidth * 0.2) / 2;
    const halfRoomDepth = (roomDepth * 0.2) / 2;
    return {
        roomWidth,
        roomDepth,
        halfRoomWidth,
        halfRoomDepth,
        rectangularDimension,
        lShapedDimension,
    };
};

// Corner L uppers keep their origin in front of the carcass, so the bounding
// box depth is shorter than the origin-to-back distance. The rectangular
// flush uses that box depth and leaves the back panel inside the wall.
// Back walls face +Z into the room, so the room face is the wall's max Z.
const cornerUpperBackFlushZ = (
    group: THREE.Group,
    wall: THREE.Mesh
): number | null => {
    if (!isCornerLShapedCabinet(group)) return null;
    if (getDirection(group.rotation.z) !== 'normal') return null;

    const cabinetBox = new THREE.Box3().setFromObject(group);
    const originToBack = group.position.z - cabinetBox.min.z;
    if (cabinetBox.isEmpty() || originToBack <= 0) return null;

    const roomFaceZ = getWallSurfaceBox(wall).max.z;
    if (!Number.isFinite(roomFaceZ)) return null;

    return roomFaceZ + originToBack;
};

export const autoRotateClamping = (
    group: THREE.Group,
    wallName: THREE.Mesh | null,
    newPosition: THREE.Vector3,
    isUpperProduct: boolean,
    roomType: string
): {
    clamped: THREE.Vector3;
    minX: number;
    maxX: number;
    minZ: number;
    maxZ: number;
} => {
    if (!wallName) {
        const clamped = newPosition.clone();
        return {
            clamped,
            minX: -Infinity,
            maxX: Infinity,
            minZ: -Infinity,
            maxZ: Infinity,
        };
    }
    const {width: latestWidth, depth: latestDepth} = getGroupWidth(group);

    const wallbox = new THREE.Box3().setFromObject(wallName);
    const wallsize = new THREE.Vector3();
    wallbox.getSize(wallsize);

    const {halfRoomWidth, halfRoomDepth} = getRoomDimensions(
        roomType as RoomType
    );

    const carcThickness =
        Number(
            (group.userData.values as RoomPlannerProduct)
                ?.cabinet_carc_thickness
        ) || 16.5;
    const values = group.userData.values as RoomPlannerProduct;
    const isLshapedProduct = group.userData.isLShapedProduct as boolean;
    const hasDoor = hasDoors(group);
    const doorThickness = hasDoor
        ? isLshapedProduct
            ? 3.3
            : Number(values.cabinet_ext_thickness) * 0.2
        : -0.4 + (isLshapedProduct ? 3.3 : 0);
    const wallThickness = 35;

    let minX = -halfRoomWidth + 31.1;
    let maxX = halfRoomWidth - latestWidth + wallThickness;
    let minZ = -halfRoomDepth + latestDepth - 15;
    let maxZ = halfRoomDepth - carcThickness;

    const direction = getDirection(group.rotation.z);
    const rectangularBackFlushZ =
        -halfRoomDepth + latestDepth - doorThickness - 12.9;
    const backWallFlushZ = () =>
        cornerUpperBackFlushZ(group, wallName) ?? rectangularBackFlushZ;

    if (isUpperProduct) {
        switch (wallName.name) {
            case 'wall:back':
                if (direction === 'back') {
                    minZ = minZ - latestDepth + 5;
                    maxZ = minZ - latestDepth + 5;
                    minX = minX + latestWidth;
                    maxX = maxX + latestWidth;
                }
                if (direction === 'normal') {
                    minZ = backWallFlushZ();
                    maxZ = minZ;
                    minX = minX;
                    maxX = maxX;
                }
                if (direction === 'left') {
                    minZ = minZ - latestDepth + 5;
                    maxZ = minZ - latestDepth + 5;
                    minX = minX + 5;
                    maxX = maxX + 5;
                }
                if (direction === 'right') {
                    minZ = minZ + 5;
                    maxZ = minZ + 5;
                    minX = minX + latestWidth - 5;
                    maxX = maxX + latestWidth - 5;
                }
                break;
            case 'wall:front':
                if (direction === 'back') {
                    minZ = maxZ - latestDepth + doorThickness + 3.7;
                    maxZ = maxZ - latestDepth + doorThickness + 3.7;
                    minX = minX + latestWidth;
                    maxX = maxX + latestWidth;
                }
                if (direction === 'normal') {
                    minZ = maxZ;
                    maxZ = maxZ;
                    minX = minX;
                    maxX = maxX;
                }
                if (direction === 'left') {
                    minZ = maxZ - latestDepth + 5;
                    maxZ = maxZ - latestDepth + 5;
                    minX = minX + 5;
                    maxX = maxX + 5;
                }
                if (direction === 'right') {
                    minZ = maxZ;
                    maxZ = maxZ;
                    minX = minX + latestWidth - 5;
                    maxX = maxX + latestWidth - 5;
                }
                break;
            case 'wall:left':
                if (direction === 'back') {
                    minZ = minZ - latestDepth + 5;
                    maxZ = maxZ - latestDepth + 5;
                    minX = minX + latestWidth;
                    maxX = minX;
                }
                if (direction === 'normal') {
                    minZ = minZ;
                    maxZ = maxZ;
                    minX = -halfRoomWidth + wallThickness;
                    maxX = -halfRoomWidth + wallThickness;
                }
                if (direction === 'left') {
                    minZ = minZ - latestDepth + 5;
                    maxZ = maxZ - latestDepth + 5;
                    minX = minX + 5;
                    maxX = minX + 5;
                }
                if (direction === 'right') {
                    minZ = minZ + 5;
                    maxZ = maxZ + 5;
                    minX = minX + latestWidth - 5;
                    maxX = minX + latestWidth - 5;
                }
                break;
            case 'wall:right':
                if (direction === 'back') {
                    minZ = minZ - latestDepth + 5;
                    maxZ = maxZ - latestDepth + 5;
                    minX = maxX + latestWidth;
                    maxX = maxX + latestWidth;
                }
                if (direction === 'normal') {
                    minZ = minZ;
                    maxZ = maxZ;
                    minX = maxX;
                    maxX = maxX;
                }
                if (direction === 'left') {
                    minZ = minZ - latestDepth;
                    maxZ = maxZ - latestDepth;
                    minX =
                        halfRoomWidth +
                        wallThickness -
                        latestWidth +
                        doorThickness;
                    maxX =
                        halfRoomWidth +
                        wallThickness -
                        latestWidth +
                        doorThickness;
                }
                if (direction === 'right') {
                    minZ = minZ + 5;
                    maxZ = maxZ + 5;
                    minX = maxX + latestWidth - 5;
                    maxX = maxX - latestWidth - 5;
                }
                break;
            case 'leftWidthWall':
                if (direction === 'back') {
                    minZ = minZ - latestDepth;
                    maxZ = maxZ - latestDepth;
                    minX = minX + latestWidth;
                    maxX = minX;
                }
                if (direction === 'normal') {
                    minZ = minZ;
                    maxZ = maxZ;
                    minX = minX;
                    maxX = minX;
                }
                if (direction === 'left') {
                    minZ = minZ - latestDepth;
                    maxZ = maxZ - latestDepth;
                    minX = minX;
                    maxX = minX;
                }
                if (direction === 'right') {
                    minZ = minZ;
                    maxZ = maxZ;
                    minX =
                        -halfRoomWidth +
                        wallThickness +
                        latestWidth -
                        doorThickness -
                        0.4;
                    maxX =
                        -halfRoomWidth +
                        wallThickness +
                        latestWidth -
                        doorThickness -
                        0.4;
                }
                break;
            case 'rightWidthWall':
                if (direction === 'back') {
                    minZ = minZ - latestDepth + 5;
                    maxZ = minZ - latestDepth + 5;
                    minX = minX + latestWidth;
                    maxX = maxX + latestWidth;
                }
                if (direction === 'normal') {
                    minZ = backWallFlushZ();
                    maxZ = minZ;
                    minX = minX;
                    maxX = maxX;
                }
                if (direction === 'left') {
                    minZ = minZ - latestDepth + 5;
                    maxZ = minZ - latestDepth + 5;
                    minX = minX + 5;
                    maxX = maxX + 5;
                }
                if (direction === 'right') {
                    minZ = minZ + 5;
                    maxZ = minZ + 5;
                    minX = minX + latestWidth - 5;
                    maxX = maxX + latestWidth - 5;
                }
                break;
            case 'leftDepthWall':
                if (direction === 'back') {
                    minZ = maxZ - latestDepth + doorThickness + 4.4;
                    maxZ = maxZ - latestDepth + doorThickness + 4.4;
                    minX = minX + latestWidth;
                    maxX = wallbox.max.x - wallsize.z;
                }
                if (direction === 'normal') {
                    minZ = maxZ;
                    maxZ = maxZ;
                    minX = minX;
                    maxX = wallbox.max.x - wallsize.z - latestWidth;
                }
                if (direction === 'left') {
                    minZ = maxZ - latestDepth + 5;
                    maxZ = maxZ - latestDepth + 5;
                    minX = minX + 5;
                    maxX = wallbox.max.x - wallsize.z - latestWidth;
                }
                if (direction === 'right') {
                    minZ = maxZ;
                    maxZ = maxZ;
                    minX = minX + latestWidth - 5;
                    maxX = wallbox.max.x - wallsize.z - 5;
                }
                break;
            case 'rightDepthWall':
                if (direction === 'back') {
                    minZ = wallbox.min.z - latestDepth + 5;
                    maxZ = wallbox.max.z - latestDepth;
                    minX = halfRoomWidth + wallThickness;
                    maxX = halfRoomWidth + wallThickness;
                }
                if (direction === 'normal') {
                    minZ = wallbox.min.z;
                    maxZ = wallbox.max.z - 5;
                    minX = maxX;
                    maxX = maxX;
                }
                if (direction === 'left') {
                    minZ = wallbox.min.z;
                    maxZ = wallbox.max.z - latestDepth;
                    minX =
                        halfRoomWidth +
                        wallThickness -
                        latestWidth +
                        doorThickness;
                    maxX =
                        halfRoomWidth +
                        wallThickness -
                        latestWidth +
                        doorThickness;
                }
                if (direction === 'right') {
                    minZ = wallbox.min.z + 5;
                    maxZ = wallbox.max.z - 5;
                    minX = wallbox.min.x + latestWidth - 5;
                    maxX = wallbox.min.x + latestWidth - 5;
                }
                break;
            case 'rightWidth2Wall':
                if (direction === 'back') {
                    minZ = wallbox.min.z - latestDepth + doorThickness + 0.4;
                    maxZ = wallbox.min.z - latestDepth + doorThickness + 0.4;
                    minX = wallbox.min.x + latestWidth - wallsize.z;
                    // Allow cabinet's right edge to reach the inner face
                    // of rightDepthWall (one wall-thickness in from the
                    // east end of rightWidth2Wall, not two).
                    maxX = wallbox.max.x - wallsize.z;
                }
                if (direction === 'normal') {
                    minZ = wallbox.min.z - 5;
                    maxZ = wallbox.min.z - 5;
                    minX = wallbox.min.x - wallsize.z;
                    maxX = wallbox.max.x - latestWidth - wallsize.z * 2;
                }
                if (direction === 'left') {
                    minZ = wallbox.min.z - latestDepth;
                    maxZ = wallbox.min.z - latestDepth;
                    minX = wallbox.min.x - wallsize.z + 5;
                    maxX = wallbox.max.x - latestWidth - wallsize.z * 2 + 5;
                }
                if (direction === 'right') {
                    minZ = wallbox.min.z;
                    maxZ = wallbox.max.z - wallsize.z;
                    minX = wallbox.min.x + latestWidth - wallsize.z;
                    maxX = wallbox.max.x - wallsize.z * 2 - 5;
                }
                break;
            case 'leftWidth2Wall':
                if (direction === 'back') {
                    minZ = wallbox.min.z + wallsize.x + 5;
                    maxZ = wallbox.max.z - latestDepth;
                    minX = wallbox.min.x;
                    maxX = wallbox.max.x - latestWidth;
                }
                if (direction === 'normal') {
                    minZ = wallbox.min.z + latestDepth + wallsize.x;
                    maxZ = wallbox.max.z - 5;
                    minX = wallbox.min.x - latestWidth;
                    maxX = wallbox.min.x - latestWidth;
                }
                if (direction === 'left') {
                    // Allow cabinet's south bbox edge to reach the south
                    // end of leftWidth2Wall (which coincides with the
                    // inner corner against rightWidth2Wall). The previous
                    // `+ wallsize.x` pushed the limit one wall-thickness
                    // north, leaving a visible gap at the inner corner.
                    minZ = wallbox.min.z;
                    maxZ = wallbox.max.z - latestDepth;
                    minX = wallbox.min.x - latestWidth + doorThickness;
                    maxX = wallbox.min.x - latestWidth + doorThickness;
                }
                if (direction === 'right') {
                    minZ = wallbox.min.z + latestDepth + wallsize.x;
                    maxZ = wallbox.max.z;
                    minX = wallbox.min.x - 5;
                    maxX = wallbox.min.x - 5;
                }
                break;
            default:
                break;
        }

        // Slide-axis correction for L-shape outer walls when the cabinet is
        // snapped (rotation matches wallRotations). The legacy bounds use
        // halfRoomWidth/halfRoomDepth (outer bounding rect) or wallbox via
        // setFromObject (inflated by dimension-arrow children), both
        // mis-aligned with the room interior, letting the cabinet phase
        // past perpendicular walls at some corners. Clamp the cabinet's
        // bbox edges to the room-side face of each perpendicular wall.
        if (roomType === 'LSHAPED' && wallName.parent) {
            const wallSiblings = wallName.parent;
            const leftW = wallSiblings.getObjectByName('leftWidthWall') as
                THREE.Mesh | undefined;
            const rightW = wallSiblings.getObjectByName('rightWidthWall') as
                THREE.Mesh | undefined;
            const leftD = wallSiblings.getObjectByName('leftDepthWall') as
                THREE.Mesh | undefined;
            const rightD = wallSiblings.getObjectByName('rightDepthWall') as
                THREE.Mesh | undefined;
            const leftW2 = wallSiblings.getObjectByName('leftWidth2Wall') as
                THREE.Mesh | undefined;
            const rightW2 = wallSiblings.getObjectByName('rightWidth2Wall') as
                THREE.Mesh | undefined;

            const groupBox = new THREE.Box3().setFromObject(group);
            const offsetToMinX = group.position.x - groupBox.min.x;
            const offsetToMaxX = groupBox.max.x - group.position.x;
            const offsetToMinZ = group.position.z - groupBox.min.z;
            const offsetToMaxZ = groupBox.max.z - group.position.z;

            if (
                wallName.name === 'leftWidthWall' &&
                direction === 'right' &&
                rightW &&
                leftD
            ) {
                // Slide Z: north end stops at rightWidthWall's room face
                // (max.z), south end stops at leftDepthWall's room face
                // (min.z).
                const rightWBox = getWallSurfaceBox(rightW);
                const leftDBox = getWallSurfaceBox(leftD);
                minZ = rightWBox.max.z + offsetToMinZ;
                maxZ = leftDBox.min.z - offsetToMaxZ;
            } else if (
                wallName.name === 'rightWidthWall' &&
                direction === 'normal' &&
                leftW &&
                rightD
            ) {
                // Slide X: west end stops at leftWidthWall's room face
                // (max.x), east end stops at rightDepthWall's room face
                // (min.x).
                const leftWBox = getWallSurfaceBox(leftW);
                const rightDBox = getWallSurfaceBox(rightD);
                minX = leftWBox.max.x + offsetToMinX;
                maxX = rightDBox.min.x - offsetToMaxX;
            } else if (
                wallName.name === 'leftDepthWall' &&
                direction === 'back' &&
                leftW &&
                leftW2
            ) {
                // Slide X: west end stops at leftWidthWall's room face
                // (max.x), east end stops at leftWidth2Wall's room face
                // (min.x).
                const leftWBox = getWallSurfaceBox(leftW);
                const leftW2Box = getWallSurfaceBox(leftW2);
                minX = leftWBox.max.x + offsetToMinX;
                maxX = leftW2Box.min.x - offsetToMaxX;
            } else if (
                wallName.name === 'rightDepthWall' &&
                direction === 'left' &&
                rightW &&
                rightW2
            ) {
                // Slide Z: north end stops at rightWidthWall's room face
                // (max.z), south end stops at rightWidth2Wall's room face
                // (min.z).
                const rightWBox = getWallSurfaceBox(rightW);
                const rightW2Box = getWallSurfaceBox(rightW2);
                minZ = rightWBox.max.z + offsetToMinZ;
                maxZ = rightW2Box.min.z - offsetToMaxZ;
            }
        }
    } else {
        const scene = wallName.parent?.parent as THREE.Scene;
        if (!scene) {
            return {
                clamped: newPosition.clone(),
                minX: -Infinity,
                maxX: Infinity,
                minZ: -Infinity,
                maxZ: Infinity,
            };
        }

        const wallGroup = scene.children.find(
            (ch) =>
                ch.name === ObjectNameMap.rectangularWall ||
                ch.name === ObjectNameMap.lShapeWall
        ) as THREE.Mesh<THREE.ExtrudeGeometry, THREE.MeshStandardMaterial>;

        if (!wallGroup) {
            return {
                clamped: newPosition.clone(),
                minX: -Infinity,
                maxX: Infinity,
                minZ: -Infinity,
                maxZ: Infinity,
            };
        }

        // Build the wall bounding box manually to avoid NaN issues
        const WallBox = new THREE.Box3();

        // Helper function to check for NaN positions
        const hasNaNPositions = (geometry: THREE.BufferGeometry): boolean => {
            if (!geometry.attributes.position) return false;
            const positions = geometry.attributes.position.array;
            for (let i = 0; i < positions.length; i++) {
                const value = positions[Number(i)];
                if (value !== value) return true; // NaN check
            }
            return false;
        };

        wallGroup.children.forEach((child) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                const geometry = mesh.geometry;
                // Check if the geometry has valid position attributes
                if (
                    geometry.attributes.position &&
                    geometry.attributes.position.count > 0 &&
                    !hasNaNPositions(geometry)
                ) {
                    geometry.computeBoundingBox();

                    if (geometry.boundingBox) {
                        const childBox = geometry.boundingBox
                            .clone()
                            .applyMatrix4(mesh.matrixWorld);
                        WallBox.union(childBox);
                    }
                }
            }
        });

        // Check if we got a valid bounding box
        if (WallBox.isEmpty()) {
            return {
                clamped: newPosition.clone(),
                minX: -Infinity,
                maxX: Infinity,
                minZ: -Infinity,
                maxZ: Infinity,
            };
        }

        // Manual panels need sufficient wall offset to ensure they spawn fully inside room
        const isManualPanel = group.userData.isManualPanel as boolean;

        // Define wall offsets per direction for manual panels
        // For regular products, use standard offset of 26
        const wallOffset = isManualPanel ? 30 : 26;

        const isNonRectShape = isNonRectShapeProduct(group);

        if (roomType === 'RECTANGULAR' && isNonRectShape) {
            const groupBox = new THREE.Box3().setFromObject(group);
            const offsetToMinX = group.position.x - groupBox.min.x;
            const offsetToMaxX = groupBox.max.x - group.position.x;
            const offsetToMinZ = group.position.z - groupBox.min.z;
            const offsetToMaxZ = groupBox.max.z - group.position.z;

            minX = WallBox.min.x + offsetToMinX + wallOffset - 1;
            maxX = WallBox.max.x - offsetToMaxX - wallOffset + 1;
            minZ = WallBox.min.z + offsetToMinZ + wallOffset - 1;
            maxZ = WallBox.max.z - offsetToMaxZ - wallOffset + 1;
        } else if (roomType === 'RECTANGULAR') {
            if (direction === 'normal') {
                minZ = WallBox.min.z + latestDepth - 4 + wallOffset;
                maxZ = WallBox.max.z - 5 - wallOffset;

                minX = WallBox.min.x + wallOffset;
                maxX = WallBox.max.x - latestWidth - wallOffset;
            }
            if (direction === 'back') {
                minZ = WallBox.min.z + 5 + wallOffset;
                maxZ = WallBox.max.z - latestDepth + 4 - wallOffset;

                minX = WallBox.min.x + latestWidth + wallOffset;
                maxX = WallBox.max.x - wallOffset;
            }
            if (direction === 'right') {
                minZ = WallBox.min.z + latestDepth + wallOffset + 2;
                maxZ = WallBox.max.z - wallOffset;

                minX = WallBox.min.x + latestWidth - 4 + wallOffset;
                maxX = WallBox.max.x - 5 - wallOffset;
            }
            if (direction === 'left') {
                minZ = WallBox.min.z + wallOffset + 2;
                maxZ = WallBox.max.z - latestDepth - wallOffset;

                minX = WallBox.min.x + 5 + wallOffset;
                maxX = WallBox.max.x - latestWidth + 2 - wallOffset;
            }
        } else if (roomType === 'LSHAPED') {
            if (!wallGroup.parent) {
                return {
                    clamped: newPosition.clone(),
                    minX: -Infinity,
                    maxX: Infinity,
                    minZ: -Infinity,
                    maxZ: Infinity,
                };
            }

            const leftWidth2Wall = (
                wallGroup.parent as THREE.Scene
            ).getObjectByName('leftWidth2Wall') as THREE.Mesh;
            const rightWidth2Wall = (
                wallGroup.parent as THREE.Scene
            ).getObjectByName('rightWidth2Wall') as THREE.Mesh;

            if (!leftWidth2Wall || !rightWidth2Wall) {
                return {
                    clamped: newPosition.clone(),
                    minX: -Infinity,
                    maxX: Infinity,
                    minZ: -Infinity,
                    maxZ: Infinity,
                };
            }

            // Use geometry-derived bboxes (see getWallSurfaceBox) so the
            // inner-wall face coordinates aren't inflated by dimension-arrow
            // children. Without this, the corner-penetration test below
            // never fires for L/Key/U panels.
            const leftWidth2Wallbox = getWallSurfaceBox(leftWidth2Wall);
            const rightWidth2Wallbox = getWallSurfaceBox(rightWidth2Wall);

            // Position-to-AABB-edge offsets, in the cabinet's current
            // orientation. Expressing every notch clamp against these plus
            // the real wall faces keeps the math origin-agnostic and
            // direction-agnostic, so rect cabinets and non-rect panels share
            // one exclusion pass instead of five per-direction variants.
            const groupBox = new THREE.Box3().setFromObject(group);
            const offsetToMinX = group.position.x - groupBox.min.x;
            const offsetToMaxX = groupBox.max.x - group.position.x;
            const offsetToMinZ = group.position.z - groupBox.min.z;
            const offsetToMaxZ = groupBox.max.z - group.position.z;

            if (isNonRectShape) {
                minX = WallBox.min.x + offsetToMinX + wallOffset - 1;
                maxX = WallBox.max.x - offsetToMaxX - wallOffset + 1;
                minZ = WallBox.min.z + offsetToMinZ + wallOffset - 1;
                maxZ = WallBox.max.z - offsetToMaxZ - wallOffset + 1;
            } else if (direction === 'normal') {
                minZ = WallBox.min.z + latestDepth - 4 + wallOffset;
                minX = WallBox.min.x + wallOffset;
                maxZ = WallBox.max.z - 5 - wallOffset;
                maxX = WallBox.max.x - latestWidth - wallOffset;
            } else if (direction === 'back') {
                minZ = WallBox.min.z + 5 + wallOffset;
                minX = WallBox.min.x + latestWidth + wallOffset;
                maxZ = WallBox.max.z - latestDepth + 4 - wallOffset;
                maxX = WallBox.max.x - wallOffset;
            } else if (direction === 'right') {
                minZ = WallBox.min.z + latestDepth + wallOffset + 2;
                minX = WallBox.min.x + latestWidth - 4 + wallOffset;
                maxZ = WallBox.max.z - wallOffset;
                maxX = WallBox.max.x - 5 - wallOffset;
            } else if (direction === 'left') {
                minZ = WallBox.min.z + wallOffset + 2;
                minX = WallBox.min.x + 5 + wallOffset;
                maxZ = WallBox.max.z - latestDepth - wallOffset;
                maxX = WallBox.max.x - latestWidth + 2 - wallOffset;
            }

            // Shared notch exclusion. Keep the cabinet's east/south bbox
            // edges out of the reentrant corner by clamping the
            // least-penetrated axis to the true inner-wall face. Replaces the
            // per-direction magic constants (-15 / -40 / -10) and the
            // wall-thickness-as-coordinate (rightWidth2Wallboxsize.z) clamps,
            // which only lined up when the notch sat near the world origin.
            const leftWallFaceX = leftWidth2Wallbox.min.x;
            const rightWallFaceZ = rightWidth2Wallbox.min.z;

            const projectedMaxX = newPosition.x + offsetToMaxX;
            const projectedMaxZ = newPosition.z + offsetToMaxZ;

            if (
                projectedMaxX > leftWallFaceX &&
                projectedMaxZ > rightWallFaceZ
            ) {
                const penetrationX = projectedMaxX - leftWallFaceX;
                const penetrationZ = projectedMaxZ - rightWallFaceZ;

                if (penetrationX > penetrationZ) {
                    const targetZ = rightWallFaceZ - offsetToMaxZ;
                    if (targetZ < maxZ) maxZ = targetZ;
                } else {
                    const targetX = leftWallFaceX - offsetToMaxX;
                    if (targetX < maxX) maxX = targetX;
                }
            } else if (projectedMaxX > leftWallFaceX) {
                const targetZ = rightWallFaceZ - offsetToMaxZ;
                if (targetZ < maxZ) maxZ = targetZ;
            } else if (projectedMaxZ > rightWallFaceZ) {
                const targetX = leftWallFaceX - offsetToMaxX;
                if (targetX < maxX) maxX = targetX;
            }
        }
    }

    const clamped = newPosition.clone();
    clamped.x = THREE.MathUtils.clamp(clamped.x, minX, maxX);
    clamped.z = THREE.MathUtils.clamp(clamped.z, minZ, maxZ);

    return {clamped, minX, maxX, minZ, maxZ};
};
